import type { BetResult, JournalEntry } from '../domain/types'
import { clamp } from './math/statistics'

export interface GreedMeterInput {
  dailyRiskUsedPct: number
  kellyFractionUsed: number
  lossesInRow: number
  openExposurePct: number
  stakeTrend: number
}

export type GreedLevel = 'cool' | 'warm' | 'hot' | 'tilt'

export interface GreedMeter {
  score: number
  level: GreedLevel
  message: string
}

export function greedMeter(input: GreedMeterInput): GreedMeter {
  const score = clamp(
    input.dailyRiskUsedPct * 0.35 +
      clamp(input.kellyFractionUsed, 0, 1) * 100 * 0.25 +
      clamp(input.lossesInRow, 0, 5) * 6 +
      input.openExposurePct * 0.2 +
      clamp(input.stakeTrend, 0, 1) * 100 * 0.15,
    0,
    100,
  )
  let level: GreedLevel = 'cool'
  if (score >= 80) level = 'tilt'
  else if (score >= 60) level = 'hot'
  else if (score >= 35) level = 'warm'

  const message =
    level === 'tilt'
      ? 'Tilt territory. Step away — this is how accounts blow up.'
      : level === 'hot'
        ? 'Running hot. Trim size and respect your limits.'
        : level === 'warm'
          ? 'Engaged but in control. Keep checking your rules.'
          : 'Cool and disciplined. This is the zone that survives.'
  return { score, level, message }
}

export interface DegenerateInput {
  tradesToday: number
  avgTradesPerDay: number
  stakeAfterLossIncreasing: boolean
  betsAboveKelly: number
  skipsIgnored: number
  lossesInRow: number
}

export interface DegenerateVerdict {
  degenerate: boolean
  score: number
  reasons: string[]
}

export function degenerateDetector(input: DegenerateInput): DegenerateVerdict {
  const reasons: string[] = []
  let score = 0

  const overtradeRatio = input.avgTradesPerDay > 0 ? input.tradesToday / input.avgTradesPerDay : input.tradesToday
  if (overtradeRatio >= 2 && input.tradesToday >= 4) {
    score += 30
    reasons.push(`You have placed ${input.tradesToday} trades today — well above your norm. Overtrading.`)
  }
  if (input.stakeAfterLossIncreasing && input.lossesInRow >= 2) {
    score += 30
    reasons.push('Stakes are rising after losses. That is chasing — the fastest way to ruin.')
  }
  if (input.betsAboveKelly > 0) {
    score += 20
    reasons.push(`${input.betsAboveKelly} position(s) are sized above full Kelly. That maximizes variance, not growth.`)
  }
  if (input.skipsIgnored > 0) {
    score += 10
    reasons.push(`You overrode ${input.skipsIgnored} "skip" recommendation(s).`)
  }
  if (input.lossesInRow >= 4) {
    score += 15
    reasons.push(`${input.lossesInRow} losses in a row. Variance or tilt — either way, cool down.`)
  }

  score = clamp(score, 0, 100)
  return { degenerate: score >= 40, score, reasons }
}

export interface Achievement {
  id: string
  name: string
  description: string
}

export const ACHIEVEMENTS: Achievement[] = [
  { id: 'took-a-skip', name: 'Took a Skip', description: 'Logged a deliberate no-trade / no-bet decision.' },
  { id: 'under-risk-limit', name: 'Stayed Under Risk Limit', description: 'Closed a day without breaching risk limits.' },
  { id: 'logged-10', name: 'Logged 10 Trades', description: 'Journaled 10 ideas with a thesis.' },
  { id: 'avoided-tilt', name: 'Avoided Tilt', description: 'No revenge/tilt entries across your last 10 logs.' },
  { id: 'beat-closing-line', name: 'Beat Closing Line', description: 'Recorded positive closing-line value.' },
  { id: 'killed-bad-thesis', name: 'Killed a Bad Thesis', description: 'Abandoned an idea after red-teaming it.' },
]

export interface BadgeProgress extends Achievement {
  earned: boolean
}

export interface BehaviorStats {
  totalLogged: number
  skips: number
  killedTheses: number
  beatCloseCount: number
  tiltInLast10: boolean
  brokeRiskLimitToday: boolean
}

export function evaluateBadges(stats: BehaviorStats): BadgeProgress[] {
  const earnedMap: Record<string, boolean> = {
    'took-a-skip': stats.skips > 0,
    'under-risk-limit': stats.totalLogged > 0 && !stats.brokeRiskLimitToday,
    'logged-10': stats.totalLogged >= 10,
    'avoided-tilt': stats.totalLogged >= 10 && !stats.tiltInLast10,
    'beat-closing-line': stats.beatCloseCount > 0,
    'killed-bad-thesis': stats.killedTheses > 0,
  }
  return ACHIEVEMENTS.map((a) => ({ ...a, earned: earnedMap[a.id] ?? false }))
}

export interface JournalStats {
  count: number
  wins: number
  losses: number
  pushes: number
  pending: number
  winRate: number
  ruleFollowRate: number
  skips: number
  tiltCount: number
  avgExpectedValue: number
  avgStake: number
  lessonsLogged: number
}

const tiltStates = new Set(['fomo', 'revenge', 'tilted'])

export function journalStats(entries: readonly JournalEntry[]): JournalStats {
  const count = entries.length
  const resolved = entries.filter((e) => e.result !== 'pending')
  const wins = entries.filter((e) => e.result === 'win').length
  const losses = entries.filter((e) => e.result === 'loss').length
  const pushes = entries.filter((e) => e.result === 'push').length
  const followed = entries.filter((e) => e.followedRules).length
  const tiltCount = entries.filter((e) => tiltStates.has(e.emotionalState)).length

  return {
    count,
    wins,
    losses,
    pushes,
    pending: count - resolved.length,
    winRate: resolved.length ? wins / resolved.length : 0,
    ruleFollowRate: count ? followed / count : 0,
    skips: entries.filter((e) => e.reasonSkip.trim().length > 0 && e.stake === 0).length,
    tiltCount,
    avgExpectedValue: count ? entries.reduce((a, e) => a + e.expectedValue, 0) / count : 0,
    avgStake: count ? entries.reduce((a, e) => a + e.stake, 0) / count : 0,
    lessonsLogged: entries.filter((e) => e.lessons.trim().length > 0).length,
  }
}

export interface RedTeamInput {
  modelProb: number
  marketProb: number
  edge: number
  dataQuality: number
  confidence: number
  liquidity: number
}

export function redTeamIdea(input: RedTeamInput): string[] {
  const args: string[] = []
  args.push(
    `The market price implies ${(input.marketProb * 100).toFixed(1)}%. Thousands of participants set that line. Why are you right and they are wrong?`,
  )
  if (input.edge < 0.04) args.push('Your edge is thin enough to be entirely explained by fees, spread, and estimation error.')
  if (input.dataQuality < 0.6) args.push('Your data quality is mediocre. Garbage in, garbage out — the probability could be far off.')
  if (input.confidence > 0.8) args.push('High confidence is itself a red flag. Overconfidence is the most common edge-killer.')
  if (input.liquidity < 0.5) args.push('Thin liquidity means your fill and exit will be worse than the quoted price.')
  args.push('Assume your thesis is already priced in. What new, non-obvious information do you actually have?')
  args.push('If this loses, what will the post-mortem say you ignored today?')
  return args
}

export interface ChecklistItem {
  id: string
  question: string
}

export const TOMORROW_CHECKLIST: ChecklistItem[] = [
  { id: 'still-believe', question: 'Would I still take this at the same price tomorrow morning?' },
  { id: 'know-invalidation', question: 'Can I state exactly what would prove me wrong?' },
  { id: 'size-ok', question: 'Is the size small enough that a loss is boring, not painful?' },
  { id: 'not-chasing', question: 'Am I entering on merit, not to recover a previous loss?' },
  { id: 'within-limits', question: 'Does this keep me within my daily and weekly risk limits?' },
  { id: 'data-quality', question: 'Is my data good enough to trust this probability?' },
]

export function checklistVerdict(answers: Record<string, boolean>): { ready: boolean; failing: string[] } {
  const failing = TOMORROW_CHECKLIST.filter((c) => !answers[c.id]).map((c) => c.id)
  return { ready: failing.length === 0, failing }
}

export function resultToPnlSign(result: BetResult): number {
  if (result === 'win') return 1
  if (result === 'loss') return -1
  return 0
}
